// src/components/KnowledgeBoard.js
// Knowledge page — per-agent library of AI-summarized sources.
// Card anatomy: header (agent scope + count + one add-menu) / toolbar (search)
// / list rows (type chip + title + AI description + quiet actions).
// Data contract (/api/files/list): name, id, s3_url, title, description —
// there is NO url field, so no URL column and no Web/File badge (see
// .claude/rules/frontend-design.md, metrics honesty).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './KnowledgeBoard.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaFile,
  FaFilePdf,
  FaFileAlt,
  FaPlus,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaTimes,
  FaLink,
  FaGlobe,
  FaExternalLinkAlt,
  FaSearch,
  FaChevronDown,
  FaBookOpen
} from 'react-icons/fa';

const fileExtension = (resource) => {
  const source = resource.name || resource.id || '';
  const match = source.match(/\.([a-z0-9]{1,5})$/i);
  return match ? match[1].toUpperCase() : null;
};

const extensionIcon = (ext) => {
  if (ext === 'PDF') return <FaFilePdf />;
  if (ext === 'TXT' || ext === 'MD') return <FaFileAlt />;
  return <FaFile />;
};

const AddResourceModal = ({ onClose, onSubmit, submitting }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = () => {
    if (!url.trim()) {
      setError(t('knowledgeBoard.fillAllFields', 'Veuillez renseigner l’URL.'));
      return;
    }
    if (!/^https?:\/\/\S+/.test(url.trim())) {
      setError(t('knowledgeBoard.invalidUrl', 'L’URL doit commencer par http:// ou https://'));
      return;
    }
    setError(null);
    onSubmit(url.trim());
  };

  return (
    <div className="kb-modal-backdrop" onClick={onClose}>
      <div className="kb-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="kb-modal-header">
          <h3>{t('knowledgeBoard.addWebPage', 'Ajouter une page web')}</h3>
          <button type="button" className="kb-modal-close" onClick={onClose} aria-label={t('knowledgeBoard.cancel', 'Annuler')}>
            <FaTimes />
          </button>
        </div>
        <div className="kb-modal-body">
          <p className="kb-modal-hint">
            {t('knowledgeBoard.addWebPageHint', 'La page sera lue et indexée dans les connaissances de l’agent — le titre et le résumé sont générés automatiquement.')}
          </p>
          <div className="kb-form-group">
            <label htmlFor="kb-res-url">{t('knowledgeBoard.resourceUrl', 'URL')}</label>
            <input
              type="text"
              id="kb-res-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              autoFocus
            />
          </div>
          {error && <div className="kb-modal-error">{error}</div>}
        </div>
        <div className="kb-modal-footer">
          <button type="button" className="u-btn u-btn--secondary" onClick={onClose} disabled={submitting}>
            {t('knowledgeBoard.cancel', 'Annuler')}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? (<><FaSpinner className="kb-spin" /> {t('knowledgeBoard.adding', 'Ajout en cours…')}</>)
              : t('knowledgeBoard.add', 'Ajouter')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ResourceViewer = ({ url, textContent, resource, onClose }) => {
  const { t } = useTranslation();
  const isText = textContent !== null;

  return (
    <div className="kb-modal-backdrop" onClick={onClose}>
      <div className="kb-modal kb-viewer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="kb-modal-header">
          <h3 title={resource?.title || resource?.name}>{resource?.title || resource?.name}</h3>
          <div className="kb-viewer-header-actions">
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer" className="kb-viewer-external">
                <FaExternalLinkAlt /> {t('knowledgeBoard.openInNewTab', 'Ouvrir dans un onglet')}
              </a>
            )}
            <button type="button" className="kb-modal-close" onClick={onClose} aria-label={t('knowledgeBoard.cancel', 'Fermer')}>
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="kb-viewer-body">
          {isText ? (
            <pre className="kb-viewer-text">{textContent}</pre>
          ) : (
            <iframe src={url} title={resource?.title || 'Resource'} className="kb-viewer-frame" />
          )}
        </div>
      </div>
    </div>
  );
};

const KnowledgeBoard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, setUser] = useState(undefined);
  const [assistants, setAssistants] = useState(null); // null = loading
  const [selectedAssistantId, setSelectedAssistantId] = useState(searchParams.get('agent') || null);
  const [resources, setResources] = useState(null); // null = loading
  const [query, setQuery] = useState(searchParams.get('q') || '');

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [feedback, setFeedback] = useState(null); // {type: 'success'|'error', text}
  const [viewer, setViewer] = useState(null); // {url, textContent, resource}
  const [deletingIds, setDeletingIds] = useState([]);

  const addMenuRef = useRef(null);
  const feedbackTimer = useRef(null);

  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  // Close the add menu on outside click
  useEffect(() => {
    if (!showAddMenu) return undefined;
    const onDocClick = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showAddMenu]);

  // Persist agent + query in the URL so refresh/Back keeps the scope
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedAssistantId) params.set('agent', selectedAssistantId); else params.delete('agent');
    if (query) params.set('q', query); else params.delete('q');
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedAssistantId, query, searchParams, setSearchParams]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchUserData(token);
    fetchAssistants(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (user === null) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (user && selectedAssistantId) {
      setResources(null);
      fetchUserResources(localStorage.getItem('access_token'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedAssistantId]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const fetchAssistants = async (token) => {
    try {
      const response = await axios.get('/api/ai-assistants', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssistants(response.data);
      const fromUrl = searchParams.get('agent');
      if (fromUrl && response.data.some((a) => a._id === fromUrl)) {
        setSelectedAssistantId(fromUrl);
        return;
      }
      const running = response.data.find((a) => a.is_running);
      if (running) setSelectedAssistantId(running._id);
      else if (response.data.length > 0) setSelectedAssistantId(response.data[0]._id);
    } catch (error) {
      console.error('Failed to fetch assistants:', error);
      setAssistants([]);
    }
  };

  const fetchUserResources = async (token) => {
    try {
      const url = selectedAssistantId
        ? `/api/files/list?assistant_id=${selectedAssistantId}`
        : '/api/files/list';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResources(response.data.files);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      setResources([]);
      showFeedback('error', t('knowledgeBoard.fetchResourcesError', 'Impossible de charger les ressources.'));
    }
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    setUploadStatuses((prev) => {
      const next = { ...prev };
      selectedFiles.forEach((f) => { next[f.name] = { uploading: true, success: false }; });
      return next;
    });

    const formData = new FormData();
    selectedFiles.forEach((f) => formData.append('files', f));

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`/api/files/upload?assistant_id=${selectedAssistantId}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      setUploadStatuses((prev) => {
        const next = { ...prev };
        for (const fileResult of response.data.files) {
          next[fileResult.filename] = fileResult.status === 'success'
            ? { uploading: false, success: true }
            : { uploading: false, success: false, error: fileResult.message };
        }
        return next;
      });
      fetchUserResources(token);
    } catch (error) {
      console.error('Upload failed:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || t('knowledgeBoard.uploadFailed', 'Échec de l’envoi.');
      setUploadStatuses((prev) => {
        const next = { ...prev };
        selectedFiles.forEach((f) => { next[f.name] = { uploading: false, success: false, error: errorMessage }; });
        return next;
      });
    }
  };

  const handleUrlFileSelect = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile) return;

    setUploadStatuses((prev) => ({ ...prev, [selectedFile.name]: { uploading: true, success: false } }));

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/api/files/upload-url-file?assistant_id=${selectedAssistantId}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      setUploadStatuses((prev) => ({ ...prev, [selectedFile.name]: { uploading: false, success: true } }));
      fetchUserResources(token);
    } catch (error) {
      console.error('Upload failed:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || t('knowledgeBoard.uploadFailed', 'Échec de l’envoi.');
      setUploadStatuses((prev) => ({ ...prev, [selectedFile.name]: { uploading: false, success: false, error: errorMessage } }));
    }
  };

  const handleAddUrl = async (url) => {
    setAddingUrl(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/files/update-web-json-2/?url=${encodeURIComponent(url)}&assistant_id=${selectedAssistantId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddModal(false);
      showFeedback('success', t('knowledgeBoard.updateSuccess', 'Page web ajoutée aux connaissances.'));
      fetchUserResources(token);
    } catch (error) {
      console.error('Failed to add web resource:', error);
      showFeedback('error', t('knowledgeBoard.updateError', 'Échec de l’ajout de la page web.'));
      setShowAddModal(false);
    } finally {
      setAddingUrl(false);
    }
  };

  const handleRemoveResourceEntry = async (resource) => {
    const confirmRemove = window.confirm(
      t('knowledgeBoard.confirmRemoveResource', 'Supprimer cette ressource des connaissances de l’agent ?')
    );
    if (!confirmRemove) return;

    setDeletingIds((prev) => [...prev, resource._id]);
    try {
      const token = localStorage.getItem('access_token');
      const title = resource.title || resource.name;
      await axios.put(
        `/api/files/delete-web-json-2/?file_id=${encodeURIComponent(resource._id)}&title=${encodeURIComponent(title)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResources((prev) => (prev || []).filter((entry) => entry._id !== resource._id));
      showFeedback('success', t('knowledgeBoard.removeResourceSuccess', 'Ressource supprimée.'));
    } catch (error) {
      console.error('Failed to remove resource entry:', error);
      showFeedback('error', t('knowledgeBoard.removeResourceError', 'Échec de la suppression.'));
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== resource._id));
    }
  };

  const fetchAndViewResource = async (resource) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/files/generate-view-url?file_key=${resource.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.type === 'text') {
        setViewer({ url: null, textContent: response.data.content, resource });
      } else {
        setViewer({ url: response.data.url, textContent: null, resource });
      }
    } catch (error) {
      console.error('Failed to view resource:', error);
      showFeedback('error', t('knowledgeBoard.viewResourceError', 'Impossible d’afficher la ressource.'));
    }
  };

  const openAddAction = (action) => {
    setShowAddMenu(false);
    if (action === 'files') document.getElementById('kbFileInput').click();
    else if (action === 'web') setShowAddModal(true);
    else if (action === 'urlFile') document.getElementById('kbUrlFileInput').click();
  };

  // ---------------------------------------------------------------- rendering

  const skeleton = (
    <div className="kb-skeleton" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => <div key={i} className="kb-skeleton-row" />)}
    </div>
  );

  if (user === undefined) {
    return (
      <div className="knowledge-board">
        <div className="kb-card">
          <div className="kb-card-header"><h1 className="kb-title">{t('knowledgeBoard.title', 'Connaissances')}</h1></div>
          {skeleton}
        </div>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="knowledge-board">
        <div className="kb-card kb-gate">
          <FaExclamationTriangle className="kb-gate-icon" />
          <p>{t('knowledgeBoard.userFetchError', 'Impossible de charger votre profil.')}</p>
        </div>
      </div>
    );
  }

  if (user.subscription_plan === 'free') {
    return (
      <div className="knowledge-board">
        <div className="kb-card kb-gate">
          <FaBookOpen className="kb-gate-icon" />
          <p>{t('knowledgeBoard.noAccessMessage', 'La base de connaissances nécessite un abonnement payant.')}</p>
          <button type="button" className="u-btn u-btn--primary" onClick={() => navigate('/Payment')}>
            {t('knowledgeBoard.upgradeNow', 'Mettre à niveau')}
          </button>
        </div>
      </div>
    );
  }

  const assistantsLoaded = assistants !== null;
  const hasAssistants = assistantsLoaded && assistants.length > 0;
  const resourcesLoaded = resources !== null;
  const list = resources || [];

  const q = query.trim().toLowerCase();
  const filtered = q
    ? list.filter((r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q))
    : list;

  const uploadEntries = Object.entries(uploadStatuses);
  const uploadsActive = uploadEntries.some(([, s]) => s.uploading);

  return (
    <div className="knowledge-board">
      <div className="kb-card">
        {/* Card header: scope + count | one add action */}
        <div className="kb-card-header">
          <div className="kb-header-left">
            <h1 className="kb-title">{t('knowledgeBoard.title', 'Connaissances')}</h1>
            {hasAssistants && (
              <div className="kb-agent-scope">
                <label htmlFor="kb-agent-select">{t('knowledgeBoard.selectAgent', 'Agent')}</label>
                <select
                  id="kb-agent-select"
                  value={selectedAssistantId || ''}
                  onChange={(e) => setSelectedAssistantId(e.target.value)}
                >
                  {assistants.map((assistant) => (
                    <option key={assistant._id} value={assistant._id}>
                      {assistant.name}{assistant.is_running ? ` ${t('knowledgeBoard.activeSuffix', '(actif)')}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {resourcesLoaded && hasAssistants && (
              <span className="kb-count">
                {t('knowledgeBoard.sourcesCount', '{{total}} sources', { total: list.length })}
              </span>
            )}
          </div>

          {hasAssistants && selectedAssistantId && (
            <div className="kb-add-wrapper" ref={addMenuRef}>
              <button
                type="button"
                className="u-btn u-btn--primary"
                aria-haspopup="menu"
                aria-expanded={showAddMenu}
                onClick={() => setShowAddMenu((v) => !v)}
              >
                <FaPlus /> {t('knowledgeBoard.addSources', 'Ajouter des sources')} <FaChevronDown className="kb-add-caret" />
              </button>
              {showAddMenu && (
                <div className="kb-add-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => openAddAction('files')}>
                    <FaFile />
                    <span>
                      {t('knowledgeBoard.addFiles', 'Fichiers')}
                      <small>{t('knowledgeBoard.addFilesHint', 'PDF, documents, texte')}</small>
                    </span>
                  </button>
                  <button type="button" role="menuitem" onClick={() => openAddAction('web')}>
                    <FaGlobe />
                    <span>
                      {t('knowledgeBoard.webPage', 'Page web')}
                      <small>{t('knowledgeBoard.addWebPageMenuHint', 'Lire et indexer une URL')}</small>
                    </span>
                  </button>
                  <button type="button" role="menuitem" onClick={() => openAddAction('urlFile')}>
                    <FaLink />
                    <span>
                      {t('knowledgeBoard.addUrlList', 'Liste d’URLs')}
                      <small>{t('knowledgeBoard.addUrlListHint', 'Fichier texte, une URL par ligne')}</small>
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {feedback && (
          <div className={`kb-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.text}
          </div>
        )}

        {/* Upload tray */}
        {uploadEntries.length > 0 && (
          <div className="kb-upload-tray">
            <div className="kb-upload-tray-header">
              <span>{t('knowledgeBoard.uploadTrayTitle', 'Envois')}</span>
              {!uploadsActive && (
                <button type="button" className="kb-upload-tray-clear" onClick={() => setUploadStatuses({})}>
                  {t('knowledgeBoard.dismiss', 'Masquer')}
                </button>
              )}
            </div>
            {uploadEntries.map(([filename, status]) => (
              <div key={filename} className="kb-upload-row">
                <span className="kb-upload-name" title={filename}>{filename}</span>
                <span className={`kb-upload-status ${status.uploading ? 'pending' : status.success ? 'ok' : 'ko'}`}>
                  {status.uploading && <><FaSpinner className="kb-spin" /> {t('knowledgeBoard.uploading', 'Envoi…')}</>}
                  {!status.uploading && status.success && <><FaCheckCircle /> {t('knowledgeBoard.uploadSuccess', 'Indexé')}</>}
                  {!status.uploading && !status.success && <><FaTimesCircle /> {status.error}</>}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar: search */}
        {hasAssistants && list.length > 0 && (
          <div className="kb-toolbar">
            <div className="kb-search">
              <FaSearch aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('knowledgeBoard.searchPlaceholder', 'Rechercher dans les titres et résumés…')}
              />
              {query && (
                <button type="button" className="kb-search-clear" onClick={() => setQuery('')} aria-label={t('knowledgeBoard.cancel', 'Effacer')}>
                  <FaTimes />
                </button>
              )}
            </div>
            {q && (
              <span className="kb-match-count">
                {t('knowledgeBoard.matchCount', '{{total}} résultats', { total: filtered.length })}
              </span>
            )}
          </div>
        )}

        {/* Content */}
        {!assistantsLoaded && skeleton}

        {assistantsLoaded && !hasAssistants && (
          <div className="kb-empty">
            <FaBookOpen className="kb-empty-icon" />
            <p>{t('knowledgeBoard.noAgents', 'Créez d’abord un agent IA : ses connaissances vivront ici.')}</p>
            <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate('/my-ai-assistants')}>
              {t('knowledgeBoard.goToAgents', 'Voir les agents IA')}
            </button>
          </div>
        )}

        {hasAssistants && !resourcesLoaded && skeleton}

        {hasAssistants && resourcesLoaded && list.length === 0 && (
          <div className="kb-empty">
            <FaBookOpen className="kb-empty-icon" />
            <p>{t('knowledgeBoard.noResources', 'Aucune source pour cet agent. Utilisez « Ajouter des sources » pour indexer des documents ou des pages web dans ses connaissances.')}</p>
          </div>
        )}

        {hasAssistants && resourcesLoaded && list.length > 0 && filtered.length === 0 && (
          <div className="kb-empty">
            <p>{t('knowledgeBoard.noMatches', 'Aucune source ne correspond à la recherche.')}</p>
          </div>
        )}

        {hasAssistants && resourcesLoaded && filtered.length > 0 && (
          <ul className="kb-list">
            {filtered.map((resource) => {
              const ext = fileExtension(resource);
              const deleting = deletingIds.includes(resource._id);
              return (
                <li key={resource._id} className="kb-row">
                  <span className="kb-type-chip" title={resource.name}>
                    {extensionIcon(ext)}
                    {ext || 'DOC'}
                  </span>
                  <button
                    type="button"
                    className="kb-row-main"
                    onClick={() => fetchAndViewResource(resource)}
                    title={resource.title || resource.name}
                  >
                    <span className="kb-row-title">{resource.title || resource.name}</span>
                    {resource.description ? (
                      <span className="kb-row-desc" title={resource.description}>{resource.description}</span>
                    ) : (
                      <span className="kb-row-desc kb-row-desc-none">{resource.name}</span>
                    )}
                  </button>
                  <div className="kb-row-actions">
                    <button
                      type="button"
                      className="kb-icon-btn"
                      onClick={() => fetchAndViewResource(resource)}
                      title={t('knowledgeBoard.view', 'Aperçu')}
                      aria-label={t('knowledgeBoard.view', 'Aperçu')}
                    >
                      <FaEye />
                    </button>
                    <button
                      type="button"
                      className="kb-icon-btn danger"
                      onClick={() => handleRemoveResourceEntry(resource)}
                      disabled={deleting}
                      title={t('knowledgeBoard.delete', 'Supprimer')}
                      aria-label={t('knowledgeBoard.delete', 'Supprimer')}
                    >
                      {deleting ? <FaSpinner className="kb-spin" /> : <FaTrash />}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Hidden file inputs */}
      <input type="file" id="kbFileInput" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
      <input type="file" id="kbUrlFileInput" onChange={handleUrlFileSelect} style={{ display: 'none' }} />

      {showAddModal && (
        <AddResourceModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUrl}
          submitting={addingUrl}
        />
      )}
      {viewer && (
        <ResourceViewer
          url={viewer.url}
          textContent={viewer.textContent}
          resource={viewer.resource}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
};

export default KnowledgeBoard;
